/** Budget · Salaries (eskiz 20): oy bo'yicha xodim maoshlari, to'landi belgisi. Tizim faqat hisobni yuritadi. */
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { whenText } from "@/features/finance/money";
import { payError, useBudgetBranches, useMarkSalariesPaid, useSalaries, useUpsertSalaries } from "@/features/budget/api";
import { money, type SalaryRow } from "@/features/budget/types";
import { periodMonth } from "@/features/dashboards/api";
import { cn, formatDate, formatMoney } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { FilterSelect, NumberInput } from "@/components/ui/field";
import { Avatar, EmptyState, ErrorState, PageLoading } from "@/components/ui/misc";
import { Table, Td, Th, Tr } from "@/components/ui/table";
import { toast } from "@/components/ui/toast";
import { ContextMenu } from "@/components/ui/context-menu";
import { copyText } from "@/lib/clipboard";
import { salaryMenu } from "@/features/budget/menu";
import { BudgetLayout, scopeLabel, useBudgetParams } from "./BudgetLayout";
import { PersonLink } from "@/components/PersonLink";
import { usePersonNav } from "@/features/people/usePersonNav";
import { PayModal, type PayResult } from "@/features/finance/PayModal";

// Matnlar: i18n namespace "budget" (salaries.*)
export function SalariesPage() {
  const { t } = useTranslation("budget");
  const roleLabel = (r: string) => t(`common:roles.${r}`, { defaultValue: r });
  const { period, branch, isOwner, me, showBranchColumn } = useBudgetParams();
  const branches = useBudgetBranches();
  const salaries = useSalaries({ period, branch });
  const upsert = useUpsertSalaries();
  const markPaid = useMarkSalariesPaid();
  const [role, setRole] = useState("");
  const [paidFilter, setPaidFilter] = useState("");
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [selected, setSelected] = useState<Set<number>>(new Set());
  // To'lov oynasi: qaysi maoshlar -- qaysi hisobdan to'lanadi
  const [payFor, setPayFor] = useState<SalaryRow[] | null>(null);
  const month = periodMonth(period);

  const rows = salaries.data?.results ?? [];
  const roles = useMemo(() => [...new Set(rows.map((r) => r.role))], [rows]);
  const filtered = rows.filter((r) => (!role || r.role === role) && (!paidFilter || (paidFilter === "paid") === r.paid));
  const dirty = Object.entries(drafts).filter(([uid, value]) => {
    const row = rows.find((r) => r.user.id === Number(uid));
    return row && value !== "" && money(value) !== money(row.amount);
  });
  // Jami valyuta bo'yicha: USD va UZS bitta songa qo'shilmaydi
  const totals = rows.reduce<Record<string, number>>((m, r) => (r.amount === null ? m : { ...m, [r.currency]: (m[r.currency] ?? 0) + money(r.amount) }), {});
  const selectable = filtered.filter((r) => r.id !== null && !r.paid && r.editable);

  async function save() {
    try {
      const items = dirty.map(([uid, amount]) => ({ user_id: Number(uid), amount, version: rows.find((r) => r.user.id === Number(uid))?.version ?? null }));
      await upsert.mutateAsync({ period, items });
      toast.success(t("salaries.saved"), t("salaries.savedSub", { count: dirty.length, month }));
      setDrafts({});
    } catch (err) {
      toast.error(payError(err, t, t("salaries.saveFailed")));
    }
  }

  async function togglePaid(row: SalaryRow, paid: boolean) {
    if (row.id === null || row.version === null) return;
    if (paid) {
      setPayFor([row]);
      return;
    }
    try {
      await markPaid.mutateAsync({ rows: [{ id: row.id, version: row.version }], paid });
      toast.success(paid ? t("salaries.markedPaid", { name: row.user.display_name }) : t("salaries.markedUnpaid", { name: row.user.display_name }));
    } catch (err) {
      toast.error(payError(err, t, t("salaries.updateFailed")));
    }
  }

  const { t: tm } = useTranslation("menu");
  const personNav = usePersonNav();
  const salaryItems = (r: SalaryRow) =>
    salaryMenu(r, isOwner, { markPaid: () => void togglePaid(r, true), markUnpaid: () => void togglePaid(r, false), copy: (text) => void copyText(text), profile: personNav }, tm);

  function markSelectedPaid() {
    setPayFor(rows.filter((r) => r.id !== null && r.version !== null && selected.has(r.id)));
  }

  async function pay(result: PayResult) {
    if (!payFor) return;
    const picked = payFor.map((r) => ({ id: r.id as number, version: r.version as number }));
    try {
      const res = await markPaid.mutateAsync({ rows: picked, paid: true, ...result });
      toast.success(payFor.length === 1 ? t("salaries.markedPaid", { name: payFor[0].user.display_name }) : t("salaries.markedCount", { count: res.updated }));
      setSelected(new Set());
      setPayFor(null);
    } catch (err) {
      toast.error(payError(err, t, t("salaries.updateFailed")));
      setPayFor(null);
    }
  }

  function toggleSelectAll(checked: boolean) {
    setSelected(checked ? new Set(selectable.map((r) => r.id as number)) : new Set());
  }

  return (
    <BudgetLayout
      subtitle={me ? t("salaries.subtitle", { scope: scopeLabel(me, branch, branches.data ?? []), month }) : undefined}
      actions={
        <Button onClick={markSelectedPaid} disabled={selected.size === 0} loading={markPaid.isPending}>
          {t("salaries.markSelected")}{selected.size > 0 ? ` (${selected.size})` : ""}
        </Button>
      }
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <FilterSelect value={role} onChange={(e) => setRole(e.target.value)} className="w-auto min-w-[170px] max-w-[280px]" aria-label={t("salaries.role")}>
          <option value="">{t("salaries.roleAll")}</option>
          {roles.map((r) => (
            <option key={r} value={r}>
              {roleLabel(r)}
            </option>
          ))}
        </FilterSelect>
        <FilterSelect value={paidFilter} onChange={(e) => setPaidFilter(e.target.value)} className="w-auto min-w-[130px] max-w-[280px]" aria-label={t("salaries.paid")}>
          <option value="">{t("salaries.paidAll")}</option>
          <option value="paid">{t("salaries.paid")}</option>
          <option value="unpaid">{t("salaries.notPaid")}</option>
        </FilterSelect>
        <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-muted">
          <Lock size={11} /> {t("salaries.visibility")}
        </span>
      </div>

      {salaries.isPending ? (
        <PageLoading />
      ) : salaries.isError ? (
        <ErrorState message={salaries.error.message} onRetry={() => salaries.refetch()} />
      ) : filtered.length === 0 ? (
        <EmptyState title={t("salaries.empty")} />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th className="w-8">
                  <input type="checkbox" className="h-4 w-4 accent-primary" aria-label={t("salaries.selectAllUnpaid")} checked={selectable.length > 0 && selected.size === selectable.length} onChange={(e) => toggleSelectAll(e.target.checked)} />
                </Th>
                <Th>{t("salaries.cols.employee")}</Th>
                <Th>{t("salaries.cols.role")}</Th>
                {showBranchColumn && <Th>{t("salaries.cols.branch")}</Th>}
                <Th className="text-right">{t("salaries.cols.amount")}</Th>
                <Th>{t("salaries.cols.paid")}</Th>
                <Th>{t("salaries.cols.paidDate")}</Th>
                <Th>{t("salaries.cols.note")}</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const draft = drafts[r.user.id];
                const value = draft ?? (r.amount !== null ? String(money(r.amount)) : "");
                const changed = draft !== undefined && draft !== "" && money(draft) !== money(r.amount);
                return (
                  <ContextMenu key={r.user.id} items={() => salaryItems(r)}>
                    <Tr className={cn(changed && "bg-warning-soft/40")}>
                      <Td>
                        {r.id !== null && !r.paid && r.editable ? (
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-primary"
                            aria-label={t("salaries.select", { name: r.user.display_name })}
                            checked={selected.has(r.id)}
                            onChange={(e) => {
                              const next = new Set(selected);
                              if (e.target.checked) next.add(r.id as number);
                              else next.delete(r.id as number);
                              setSelected(next);
                            }}
                          />
                        ) : null}
                      </Td>
                      <Td>
                        <span className="flex items-center gap-2">
                          <Avatar initials={r.user.initials} name={r.user.full_name} size={26} />
                          <PersonLink id={r.user.id} name={r.user.full_name} role={r.role} className="font-semibold" />
                        </span>
                      </Td>
                      <Td>{roleLabel(r.role)}</Td>
                      {showBranchColumn && <Td>{r.branch_name ?? "—"}</Td>}
                      <Td className="text-right">
                        {r.paid || !r.editable ? (
                          <span className="font-semibold" title={!r.editable ? t("salaries.ownerSets") : undefined}>
                            {r.amount !== null ? formatMoney(money(r.amount), r.currency) : <span className="font-normal text-muted">—</span>}
                          </span>
                        ) : (
                          <NumberInput
                            min={0}
                            step="1"
                            value={value}
                            onChange={(e) => setDrafts((d) => ({ ...d, [r.user.id]: e.target.value }))}
                            placeholder={r.suggested !== null ? t("salaries.lastMonth", { amount: money(r.suggested) }) : "0"}
                            className="w-[150px] text-right"
                            aria-label={t("salaries.salaryAria", { name: r.user.full_name })}
                          />
                        )}
                      </Td>
                      <Td>
                        <PaidToggle checked={r.paid} disabled={!r.editable || r.id === null || markPaid.isPending || (r.paid && !isOwner)} onChange={(v) => togglePaid(r, v)} title={!r.editable ? t("salaries.ownerSets") : r.id === null ? t("salaries.enterFirst") : r.paid && !isOwner ? t("salaries.ownerOnlyUnpaid") : undefined} />
                      </Td>
                      <Td className="text-[12px] text-ink-soft">
                        {r.paid_at ? (r.payment?.at ? whenText({ date: r.paid_at, at: r.payment.at }) : formatDate(r.paid_at)) : <span className="text-muted">—</span>}
                        {r.payment && <div className="text-[11px] text-muted">{r.payment.account}</div>}
                      </Td>
                      <Td className="text-[12px] text-muted">{r.note || t("salaries.bankTransfer")}</Td>
                    </Tr>
                  </ContextMenu>
                );
              })}
              <Tr className="bg-fill/50">
                <Td />
                <Td className="font-bold" colSpan={isOwner && !branch ? 4 : 3}>
                  {t("salaries.totalRow", { count: rows.length, paid: salaries.data?.paid_count ?? 0 })}
                </Td>
                <Td className="text-right font-bold whitespace-nowrap">
                  {Object.entries(totals)
                    .map(([c, v]) => formatMoney(v, c))
                    .join(" · ") || "—"}
                </Td>
                <Td colSpan={3} />
              </Tr>
            </tbody>
          </Table>
          <p className="mt-2 text-[12px] text-muted">{t("salaries.note")}</p>
        </>
      )}

      {/* Saqlash paneli ekranning pastki o'ng burchagida turadi: `body`da chiziladi, sahifa o'rami (animatsiya,
          transform) `position: fixed`ni surib yubormaydi (2026-10-18: panel sahifaning eng pastiga tushib qolardi) */}
      {dirty.length > 0 &&
        createPortal(
          <div className="fixed right-6 bottom-6 z-40 flex items-center gap-3 rounded-2xl border border-border bg-surface px-4 py-3 shadow-menu">
            <span className="text-[13px]">{t("salaries.unsaved", { count: dirty.length })}</span>
            <Button variant="ghost" onClick={() => setDrafts({})}>
              {t("salaries.discard")}
            </Button>
            <Button variant="primary" loading={upsert.isPending} onClick={save}>
              {t("salaries.save")}
            </Button>
          </div>,
          document.body,
        )}
      <PayModal
        open={!!payFor}
        onOpenChange={(v) => !v && setPayFor(null)}
        title={t("pay.salaryTitle", { count: payFor?.length ?? 0 })}
        description={payFor?.length === 1 ? payFor[0].user.display_name : undefined}
        amounts={(payFor ?? []).map((r) => ({ amount: money(r.amount), currency: r.currency }))}
        branch={branch}
        allowNoAccount
        confirmLabel={t("pay.markPaid")}
        loading={markPaid.isPending}
        onConfirm={pay}
      />
    </BudgetLayout>
  );
}

function PaidToggle({ checked, disabled, onChange, title }: { checked: boolean; disabled?: boolean; onChange: (v: boolean) => void; title?: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      title={title}
      onClick={() => onChange(!checked)}
      className={cn("relative h-[22px] w-10 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-60", checked ? "bg-primary" : "bg-border")}
    >
      <span className={cn("absolute top-[2px] h-[18px] w-[18px] rounded-full bg-surface transition-all", checked ? "left-[20px]" : "left-[2px]")} />
    </button>
  );
}
